/* @moldura padrao */
/* =============================================================================
   ⚠️ VERSÃO ANTIGA — CONGELADA EM 25/08/2026, GUARDADA PARA CONFERÊNCIA
   =============================================================================
   Esta é a tela como ela era ANTES da limpeza de 25/08. Ela **não é a tela
   oficial** — a oficial é a irmã sem o sufixo, e é ela que responde na rota de
   sempre. Esta responde em `-antiga` e existe por um motivo só, dado por ele:

     *"se lá na frente a gente detectar que teve alguma coisa que saiu de ordem,
     a gente pode consultar a antiga."*

   NÃO CORRIJA NADA AQUI. Se algo estiver errado nela, o conserto é na oficial —
   o valor deste arquivo é ser exatamente o que estava no ar, defeitos inclusos.
   Ele carrega o CSS numa string de template (`CSS_LOCAL`/`CSS_BARRA`) e as
   classes `card-barra-*` / `evento-barra-*`, que na oficial viraram
   `casaa-cartao-agendamento`.

   ⏳ PENDÊNCIA COMBINADA: apagar as duas telas `-antiga` (e suas rotas) quando o
   redesenho fechar e estiver tudo conferido. Está na ficha da conversa.
   ============================================================================= */
/**
 * instalacoes.tsx (agenda) — A AGENDA DO INSTALADOR. Uma semana, e só.
 *
 * TELA PRÓPRIA, e não a de Agendamentos com permissões (decisão dele,
 * 09/08/2026). O trabalho é outro: a Suany MARCA — precisa de "OS a agendar",
 * arrastar, editar, imprimir. O instalador só precisa saber **onde ir e o que
 * fazer**, com o celular na mão, dentro da caminhonete. Uma tela que serve aos
 * dois acaba servindo mal a um.
 *
 * DOIS MODOS, e ele escolhe: CALENDÁRIO (o FullCalendar da tela de
 * Agendamentos, limitado a uma semana) e LISTA (um bloco por dia). A lista
 * nasceu primeiro por caber melhor em tela estreita; o calendário entrou porque
 * o Vittorio quis ver a semana como semana — "está horroroso", disse ele da
 * lista sozinha (09/08/2026). O calendário é o padrão; a lista continua a um
 * toque, e é ela que se lê bem no celular apertado.
 *
 * SÓ O QUE A SUANY LIBEROU. Ele não deve saber com muita antecedência o que
 * vai fazer — regra do Vittorio. Até 09/08/2026 isso era uma CONSTANTE aqui no
 * código (`PROXIMA_SEMANA_LIBERADA = false`), o que dava o horizonte certo pela
 * razão errada: quem decide é quem agenda, não o front do instalador. Agora a
 * liberação é dado (migration 047), marcada por ela na tela de Agendamentos em
 * três níveis — card, dia ou semana.
 *
 * ESTA TELA NÃO FILTRA NADA. O filtro é do BACKEND: ela pede
 * `GET /visitas?visao=instalador`, e para o perfil `instalador` o filtro vale
 * de qualquer jeito. Aberta pela Suany ou pelo admin, mostra o mesmo que ele
 * veria — é assim que se confere a liberação sem trocar de login. Se um dia isto virar responsa da
 * tela, a regra passa a depender de quem consome — que é justamente o que se
 * quis evitar.
 *
 * A PRÓXIMA SEMANA aparece quando ela libera alguma coisa nela: o botão deixa
 * de ser decorativo e passa a refletir o que existe do outro lado.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import ptBr from '@fullcalendar/core/locales/pt-br';
import { api, ApiError } from '@/lib/api';
import { nomeCurtoDaViagem, roteiroCompleto } from '@/lib/viagem';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

/* Estilo "Barra" do card, igual ao da tela de Agendamentos: fundo cinza discreto
   e a cor do TIPO na borda esquerda de verdade (colada, não flutuando). Cor por
   classe e por token do tema — nada de hex. Duplicado aqui de propósito: as duas
   telas são independentes, e uma folha compartilhada faria mexer numa mudar a
   outra sem querer. */
const CSS_BARRA = `
.fc .fc-event.evento-barra {
  background-color: var(--bs-secondary-bg);
  border-color: var(--bs-border-color);
  border-left-width: 4px;
  color: var(--adminuiux-text);
  cursor: pointer;
}
.fc .fc-event.evento-barra-blue   { border-left-color: var(--bs-blue); }
.fc .fc-event.evento-barra-orange { border-left-color: var(--bs-orange); }
.fc .fc-event.evento-barra-cyan   { border-left-color: var(--bs-cyan); }
.fc .fc-event.evento-barra-green  { border-left-color: var(--bs-green); }
.fc .fc-event.evento-barra-teal   { border-left-color: var(--bs-teal); }
.fc .fc-event.evento-barra-indigo { border-left-color: var(--bs-indigo); }
.fc .fc-event.evento-barra-purple { border-left-color: var(--bs-purple); }
.fc .fc-event.evento-barra-yellow { border-left-color: var(--bs-yellow); }
.fc .fc-event.evento-barra-gray   { border-left-color: var(--bs-gray); }
/* MINI-CALENDÁRIO (react-datepicker, o mesmo componente dos campos de data —
   o tema já o estiliza inteiro em _calendar.scss). Três marcas, e cada uma
   responde a uma pergunta diferente:
     .semana-atual  — a faixa da semana que está na tela ("onde eu estou")
     .dia-servico   — tem serviço marcado (bolinha embaixo do número)
     .dia-viagem    — o dia é de viagem (bolinha na cor da viagem)
   A bolinha vai por ::after para não empurrar o número nem mudar a altura da
   linha, que é o que quebra a grade do datepicker. */
.mini-agenda .react-datepicker,
.mini-agenda .react-datepicker__header,
.mini-agenda .react-datepicker__month-container {
  /* TRANSPARENTE, e com !important porque o tema também usa.
     O _calendar.scss pinta o datepicker de branco 90% com sombra e blur — e
     está certo LA: o datepicker do tema FLUTUA sobre a tela, como popover de um
     campo de data, e precisa de fundo próprio para se destacar. Aqui ele está
     EMBUTIDO num card; o fundo próprio virava uma placa branca com sombra
     dentro de outro cartão, que foi o que o Vittorio viu ("ficou horroroso").
     Herdando o card, ele deixa de ser caixa e vira conteúdo.
     ATENÇÃO: nada de crase neste bloco — ele mora dentro de um template string
     (foi o que quebrou aqui em 09/08/2026; ver memoria teste-1-css-local-crase). */
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  backdrop-filter: none !important;
}
/* O CARTAO do mini-calendario tambem some (Vittorio, 09/08: "o painel esta com o
   fundo todo branco, deveria ser transparente"). O passo anterior tirou o branco
   do datepicker, mas sobrou o branco do proprio card do tema por baixo — medido
   no DOM: .mini-agenda = rgb(255,255,255). Sem fundo e sem sombra ele deixa de
   ser placa e vira parte da pagina; a linha do cabecalho continua clicavel e o
   chevron continua dizendo que abre. Vale nos dois temas, porque transparente
   herda o fundo do corpo em vez de cravar cor. */
.mini-agenda.card.adminuiux-card {
  background-color: transparent;
  box-shadow: none;
  border: 0;
}
.mini-agenda .react-datepicker { width: 100%; }
.mini-agenda .react-datepicker__month-container { width: 100%; }
.mini-agenda .react-datepicker__day { position: relative; }
.mini-agenda .react-datepicker__day.semana-atual {
  background-color: rgba(var(--adminuiux-theme-1-rgb), 0.12);
  border-radius: 0;
}
.mini-agenda .react-datepicker__day.dia-servico::after,
.mini-agenda .react-datepicker__day.dia-viagem::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: 2px;
  transform: translateX(-50%);
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--bs-blue);
}
.mini-agenda .react-datepicker__day.dia-viagem::after { background-color: var(--bs-purple); }

/* O NÚMERO DO DIA É O QUE SE PROCURA ao rolar a semana — estava do tamanho de
   legenda. Cresce; o nome do dia fica como está e centraliza ao lado dele. */
.dia-numero {
  min-width: 2.25rem;
  height: 2.25rem;
  font-size: 1.15rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--bs-border-radius);
}

.ponto-legenda { width: 6px; height: 6px; border-radius: 50%; display: inline-block; }
.ponto-servico { background: var(--bs-blue); }
.ponto-viagem  { background: var(--bs-purple); }

/* O CARD DA LISTA É O MESMO DO CALENDÁRIO. Mesmo fundo discreto, mesma borda,
   mesma barra colorida à esquerda — o instalador troca de modo e reconhece o
   card, em vez de aprender dois desenhos para a mesma coisa. O que muda é só o
   que CABE: na lista sobra largura para endereço inteiro e pauta. */
.card-barra {
  background-color: var(--bs-secondary-bg);
  border: 1px solid var(--bs-border-color);
  border-left: 4px solid var(--bs-gray);
  color: var(--adminuiux-text);
  cursor: pointer;
}
.card-barra-blue   { border-left-color: var(--bs-blue); }
.card-barra-orange { border-left-color: var(--bs-orange); }
.card-barra-cyan   { border-left-color: var(--bs-cyan); }
.card-barra-green  { border-left-color: var(--bs-green); }
.card-barra-teal   { border-left-color: var(--bs-teal); }
.card-barra-indigo { border-left-color: var(--bs-indigo); }
.card-barra-purple { border-left-color: var(--bs-purple); }
.card-barra-yellow { border-left-color: var(--bs-yellow); }
.card-barra-gray   { border-left-color: var(--bs-gray); }
`;

registerLocale('pt-BR', ptBR);

type Modo = 'calendario' | 'lista';

interface Visita {
  id: number;
  os_id: number | null;
  os_codigo: string | null;
  os_tipo: string | null;
  tipo: string;
  titulo: string | null;
  /** 'YYYY-MM-DD'. A hora vem à parte — não é um timestamp. */
  data: string;
  /** 'HH:MM', ou null quando o evento é de dia inteiro. */
  hora: string | null;
  /** Preenchido quando a ida faz parte de uma viagem. */
  viagem_id: number | null;
  dia_inteiro: boolean;
  status: string;
  pauta: string | null;
  cliente_nome: string | null;
  endereco_resumo: string | null;
  bairro_cidade: string | null;
  loja_codigo: string | null;
}

/** Cor da barra por tipo — as MESMAS da tela de Agendamentos, para o instalador
 *  reconhecer o card que a Suany montou. Token do Bootstrap, sem hex. */
const COR_OS: Record<string, string> = {
  instalacao: 'blue',
  manutencao: 'orange',
  lavagem: 'cyan',
};
const COR_EVENTO: Record<string, string> = {
  entrega: 'green',
  retirada: 'teal',
  servico: 'indigo',
  ausencia: 'gray',
  reuniao: 'purple',
  medicao: 'pink',
  dia_sem_os: 'gray',
  lembrete: 'yellow',
};
const ABREV_OS: Record<string, string> = {
  instalacao: 'INS',
  manutencao: 'MAN',
  lavagem: 'LAV',
};

function corDo(v: Visita): string {
  if (v.os_id) return v.os_tipo ? (COR_OS[v.os_tipo] ?? 'gray') : 'gray';
  return COR_EVENTO[v.tipo] ?? 'yellow';
}

interface Viagem {
  id: number;
  /** Como ela se chama onde o espaço é pouco. Vazio = usa a sugestão (048). */
  nome_curto: string | null;
  destinos: string[];
  partida: string | null;
  chegada: string | null;
}

const ROTULO_TIPO: Record<string, string> = {
  instalacao: 'Instalação',
  manutencao: 'Manutenção',
  lavagem: 'Lavagem',
  entrega: 'Entrega',
  retirada: 'Retirada',
  servico: 'Serviço',
  reuniao: 'Reunião',
  lembrete: 'Lembrete',
};

/**
 * A segunda-feira da semana que interessa.
 *
 * NO DOMINGO, OLHA PARA A FRENTE. Domingo é folga, e quem abre o app nele quer
 * saber o que vem — não o que passou. Se o domingo pertencesse à semana que
 * termina (a convenção ISO), o instalador abriria a tela e veria os serviços já
 * feitos, com "nada marcado" à vista para amanhã. Descoberto testando num
 * domingo, 09/08/2026.
 *
 * Consequência aceita: trabalho marcado NO domingo não aparece nesse mesmo dia.
 * É raro, e a alternativa (mostrar a semana morta) atrapalha todo domingo.
 */
function segundaDa(d: Date): Date {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const dow = x.getDay();
  if (dow === 0) {
    x.setDate(x.getDate() + 1); // domingo → a segunda de amanhã
    return x;
  }
  x.setDate(x.getDate() - (dow - 1));
  return x;
}

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

export function AgendaInstalacoesAntigaPage() {
  const [visitas, setVisitas] = useState<Visita[]>([]);
  const [viagens, setViagens] = useState<Viagem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  /** A Suany já liberou alguma coisa na semana seguinte? É o que habilita o
   *  botão "Próxima semana" — antes isso era uma constante no código. */
  const [proximaLiberada, setProximaLiberada] = useState(false);
  /**
   * Calendário no computador, lista no celular — e ele troca à vontade.
   *
   * Não é preferência estética: `dayGridWeek` em 390px dá sete colunas de 48px,
   * onde "TESTE AGO — Beatriz Nogueira, Geribá" vira "TEST Geri". A lista usa a
   * largura toda e mostra endereço inteiro, que é o que o instalador foi
   * buscar. No computador é o contrário: a semana como semana se lê de uma vez,
   * e foi o que o Vittorio pediu.
   *
   * Escolha só no primeiro render: trocar de modo sozinho ao girar o telefone
   * seria pior que qualquer padrão.
   */
  // LISTA é o padrão, nas duas telas (decisão dele, 09/08/2026). No celular ela
  // é a única legível; no computador ele preferiu assim mesmo.
  const [modo, setModo] = useState<Modo>('lista');
  // Fim de semana: fica DESLIGADO por padrão porque sábado é exceção. O botão
  // aparece do lado do seletor de modo, como na tela de Agendamentos.
  const [fimDeSemana, setFimDeSemana] = useState(false);
  /** Card aberto. É só leitura — o instalador não edita nada aqui. */
  const [detalhe, setDetalhe] = useState<Visita | null>(null);
  // O mini-calendário nasce FECHADO: é consulta, não o assunto da tela. Quem
  // abre o app quer a lista do dia; o calendário é para quem foi se situar.
  const [miniAberto, setMiniAberto] = useState(false);
  /**
   * Dias recolhidos à mão. O que JÁ PASSOU nasce recolhido sozinho (ver
   * `recolhido()`), e este conjunto só guarda o que ele abriu ou fechou depois
   * — assim a regra automática continua valendo para o resto da semana sem
   * precisar mexer em nada.
   */
  const [alternados, setAlternados] = useState<Set<string>>(new Set());

  const alternarDia = (chave: string) =>
    setAlternados((s) => {
      const n = new Set(s);
      if (n.has(chave)) n.delete(chave);
      else n.add(chave);
      return n;
    });

  const hoje = useMemo(() => new Date(), []);
  /* Qual semana está na tela: 0 = esta. Só passa de 0 se a Suany tiver liberado
     algo na semana seguinte — o botão fica desabilitado até lá. */
  const [semanasAdiante, setSemanasAdiante] = useState(0);
  const segunda = useMemo(() => {
    const d = segundaDa(hoje);
    d.setDate(d.getDate() + semanasAdiante * 7);
    return d;
  }, [hoje, semanasAdiante]);
  // Domingo fecha a semana: eles às vezes trabalham sábado, e um card invisível
  // é pior que um dia vazio.
  const domingo = useMemo(() => {
    const d = new Date(segunda);
    d.setDate(d.getDate() + 6);
    return d;
  }, [segunda]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const seg2 = new Date(segunda);
      seg2.setDate(seg2.getDate() + 7);
      const dom2 = new Date(seg2);
      dom2.setDate(dom2.getDate() + 6);
      const [vs, vgs, proxima] = await Promise.all([
        api.get<Visita[]>(
          `/agenda/visitas?de=${iso(segunda)}&ate=${iso(domingo)}&visao=instalador`,
        ),
        // As viagens dão o CONTEXTO do dia: "isto é uma ida a Trancoso", não
        // três paradas soltas. O card sozinho não conta essa parte.
        api.get<Viagem[]>('/viagens?tipo=operacional').catch(() => []),
        // Sonda da semana seguinte: como o backend já filtra pelo perfil, vir
        // vazio significa "ela ainda não liberou nada lá" — é exatamente o que
        // o botão precisa saber, sem endpoint novo.
        api
          .get<Visita[]>(`/agenda/visitas?de=${iso(seg2)}&ate=${iso(dom2)}&visao=instalador`)
          .catch(() => []),
      ]);
      setVisitas(vs);
      setViagens(vgs);
      setProximaLiberada(proxima.length > 0);
    } catch (err) {
      setErro(
        err instanceof ApiError ? err.message : 'Não foi possível carregar a semana.',
      );
    } finally {
      setCarregando(false);
    }
  }, [segunda, domingo]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** Os sete dias, cada um com suas visitas em ordem de hora. */
  const dias = useMemo(() => {
    const out: { data: Date; chave: string; itens: Visita[] }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(segunda);
      d.setDate(d.getDate() + i);
      const chave = iso(d);
      out.push({
        data: d,
        chave,
        // Com hora primeiro, em ordem; sem hora depois. Hora é exceção na
        // operação, mas quando existe é ela que manda no roteiro do dia.
        itens: visitas
          .filter((v) => (v.data ?? '').slice(0, 10) === chave)
          .sort((a, b) => (a.hora ?? '99:99').localeCompare(b.hora ?? '99:99')),
      });
    }
    return out;
  }, [visitas, segunda]);

  /* O nome CURTO da viagem — o que cabe no chip do card, no celular, que é onde
     esta tela vive. O roteiro por extenso vai no `title` (nomeViagemCompleto). */
  const nomeViagem = useCallback(
    (id: number | null | undefined): string | null => {
      if (!id) return null;
      const vg = viagens.find((x) => x.id === id);
      if (!vg) return null;
      return nomeCurtoDaViagem(vg.nome_curto, vg.destinos) || `Viagem #${vg.id}`;
    },
    [viagens],
  );

  const nomeViagemCompleto = useCallback(
    (id: number | null | undefined): string | undefined => {
      if (!id) return undefined;
      const vg = viagens.find((x) => x.id === id);
      if (!vg) return undefined;
      return roteiroCompleto(vg.destinos) || undefined;
    },
    [viagens],
  );

  /** Dias com serviço e dias de viagem — o que o mini-calendário marca. */
  const diasMarcados = useMemo(() => {
    const servico = new Set<string>();
    const viagem = new Set<string>();
    for (const v of visitas) {
      const dia = (v.data ?? '').slice(0, 10);
      if (!dia) continue;
      servico.add(dia);
      if (v.viagem_id) viagem.add(dia);
    }
    return { servico, viagem };
  }, [visitas]);

  const hojeIso = iso(hoje);
  const total = visitas.length;

  /**
   * Dia passado nasce RECOLHIDO — quem abre a agenda na quarta quer a quarta, e
   * segunda e terça só ocupam a tela até ele rolar. O clique no cabeçalho abre
   * (ou fecha o que estiver aberto), e essa escolha manual vence a regra.
   */
  const recolhido = (chave: string) => {
    const passou = chave < hojeIso;
    return alternados.has(chave) ? !passou : passou;
  };

  /** As mesmas visitas, no formato que o FullCalendar entende. */
  const eventos = useMemo(
    () =>
      visitas.map((v) => ({
        id: String(v.id),
        // Sem hora, o evento é do dia inteiro — o FullCalendar decide por aqui.
        start: v.hora ? `${v.data}T${v.hora}:00` : v.data,
        allDay: !v.hora,
        title: v.cliente_nome ?? v.titulo ?? 'Serviço',
        extendedProps: v,
      })),
    [visitas],
  );

  return (
    /* A lista se lê melhor estreita (linha curta cansa menos); o calendário
       quer largura — sete colunas espremidas em 720px truncam tudo. Isso deixou
       de ser um `style` cravado aqui e virou a largura `leitura` da moldura,
       porque apareceu o segundo uso (esta tela e a irmã de celular). */
    <CustomMolduraPagina
      largura={modo === 'lista' ? 'leitura' : 'padrao'}
      titulo="Instalações da semana"
      descricao={
        `${segunda.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} a ` +
        `${domingo.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })} · ` +
        (total === 0 ? 'nada marcado' : total === 1 ? '1 serviço' : `${total} serviços`)
      }
      acoes={
      <>
        <style>{CSS_BARRA}</style>
          {/* Calendário x Lista. O calendário mostra a semana como semana; a
              lista é o que se lê melhor em tela estreita. */}
          <div className="btn-group btn-group-sm me-2" role="group">
            <button
              type="button"
              className={`btn ${modo === 'lista' ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => setModo('lista')}
            >
              <i className="bi bi-list-ul me-1"></i>Lista
            </button>
            <button
              type="button"
              className={`btn ${modo === 'calendario' ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => setModo('calendario')}
            >
              <i className="bi bi-calendar-week me-1"></i>Calendário
            </button>
          </div>
          {/* RÓTULO FIXO, estado na cor. Antes o texto mudava junto com o
              estado ("Seg–Sex" ↔ "Seg–Dom") e ninguém sabia se estava lendo o
              que ESTÁ ligado ou o que o clique VAI fazer — ainda mais ao lado de
              Lista/Calendário, onde o botão aceso é o modo atual. Agora o texto
              nomeia a coisa e o aceso diz que ela está ligada, igual aos outros. */}
          <button
            type="button"
            className={`btn btn-sm me-2 ${fimDeSemana ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => setFimDeSemana((v) => !v)}
            title={fimDeSemana ? 'Esconder sábado e domingo' : 'Mostrar sábado e domingo'}
          >
            <i className="bi bi-calendar2-week me-1"></i>Sáb e dom
          </button>
          {semanasAdiante > 0 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-theme me-2"
              onClick={() => setSemanasAdiante((n) => Math.max(0, n - 1))}
              title="Voltar para a semana atual"
            >
              <i className="bi bi-chevron-left"></i> Semana atual
            </button>
          )}
          <button
            type="button"
            className="btn btn-sm btn-outline-theme"
            disabled={!proximaLiberada}
            onClick={() => setSemanasAdiante((n) => n + 1)}
            title={
              proximaLiberada
                ? 'Ver a próxima semana'
                : 'A próxima semana ainda não está liberada'
            }
          >
            Próxima semana <i className="bi bi-chevron-right"></i>
          </button>
      </>
      }
    >

      {/* MINI-CALENDÁRIO. Serve para SE SITUAR — "que semana é essa, onde tem
          serviço" — e não para navegar: o horizonte de uma semana é regra, e
          por isso os dias fora dela ficam desabilitados. Fica acima do conteúdo
          no celular e ao lado no computador. */}
      {/* MINI-CALENDÁRIO — no TOPO e FECHADO por padrão (pedido dele). É
          consulta: quem abre o app quer a lista do dia; o calendário serve a
          quem foi se situar ("que semana é essa, onde tem serviço"). Clicar num
          dia ROLA a tela até ele — é a única navegação que faz sentido aqui,
          já que a semana é fixa. */}
      <div className="card adminuiux-card mb-3 mini-agenda">
        <div
          className="card-body py-2 d-flex align-items-center gap-2"
          role="button"
          onClick={() => setMiniAberto((v) => !v)}
        >
          <i className="bi bi-calendar3 text-secondary"></i>
          <span className="small fw-medium">Calendário do mês</span>
          <span className="small text-secondary d-none d-sm-inline">
            · {diasMarcados.servico.size} dia(s) com serviço nesta semana
          </span>
          <i className={`bi ms-auto ${miniAberto ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
        </div>
        {miniAberto && (
          <div className="card-body pt-0">
            <div className="d-flex justify-content-center">
              <DatePicker
                inline
                locale="pt-BR"
                /* SEM dia selecionado: este calendário informa, não escolhe. */
                selected={null}
                openToDate={segunda}
                minDate={segunda}
                maxDate={domingo}
                onChange={(d) => {
                  if (!d) return;
                  const alvo = document.getElementById(`dia-${iso(d)}`);
                  alvo?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                dayClassName={(d) => {
                  const k = iso(d);
                  const dentro = k >= iso(segunda) && k <= iso(domingo);
                  return [
                    dentro ? 'semana-atual' : '',
                    diasMarcados.viagem.has(k)
                      ? 'dia-viagem'
                      : diasMarcados.servico.has(k)
                        ? 'dia-servico'
                        : '',
                  ]
                    .filter(Boolean)
                    .join(' ');
                }}
              />
            </div>
            <div className="d-flex justify-content-center gap-3 small text-secondary mt-1">
              <span className="d-inline-flex align-items-center gap-1">
                <span className="ponto-legenda ponto-servico" /> serviço
              </span>
              <span className="d-inline-flex align-items-center gap-1">
                <span className="ponto-legenda ponto-viagem" /> viagem
              </span>
            </div>
          </div>
        )}
      </div>

      {carregando ? (
        <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
          <span className="spinner-border spinner-border-sm" /> Carregando…
        </div>
      ) : erro ? (
        <div className="alert alert-warning">{erro}</div>
      ) : total === 0 ? (
        <div className="card adminuiux-card">
          <div className="card-body text-center py-5 text-secondary">
            <i className="bi bi-calendar-check display-6 d-block mb-2"></i>
            Nada marcado para esta semana.
          </div>
        </div>
      ) : modo === 'calendario' ? (
        <div className="card adminuiux-card">
          <div className="card-body">
            {/* O MESMO FullCalendar da tela de Agendamentos, limitado a UMA
                semana e sem nada de editar: `dayGridWeek`, sem `editable`, sem
                drag, sem barra de navegação (a semana é a desta tela e ponto —
                o horizonte é a regra, não uma preferência de quem olha). */}
            <FullCalendar
              plugins={[dayGridPlugin]}
              initialView="dayGridWeek"
              initialDate={iso(segunda)}
              /* Semana começa na SEGUNDA. O padrão do FullCalendar é domingo, e
                 aí a grade ia de dom 09 a sáb 15 enquanto os dados buscados vão
                 de seg 10 a dom 16: sobrava um domingo vazio na frente e sumia
                 o do fim. */
              firstDay={1}
              locale={ptBr}
              height="auto"
              weekends={fimDeSemana}
              headerToolbar={false}
              dayMaxEvents={false}
              events={eventos}
              /* Mesmas classes da tela de Agendamentos: fundo discreto e a cor
                 do tipo na BORDA ESQUERDA. O instalador reconhece o card que a
                 Suany montou. */
              eventClassNames={(arg) => {
                const v = arg.event.extendedProps as Visita;
                return ['evento-barra', `evento-barra-${corDo(v)}`];
              }}
              eventClick={(arg) => setDetalhe(arg.event.extendedProps as Visita)}
              eventContent={(arg) => {
                const v = arg.event.extendedProps as Visita;
                return (
                  <div className="p-1 lh-sm overflow-hidden">
                    {v.hora && <div className="fw-semibold small">{v.hora}</div>}
                    <div className="small text-truncate">
                      {v.cliente_nome ?? v.titulo ?? 'Serviço'}
                    </div>
                    {v.bairro_cidade && (
                      <div className="small text-truncate opacity-75">
                        {v.bairro_cidade}
                      </div>
                    )}
                  </div>
                );
              }}
            />
          </div>
        </div>
      ) : (
        dias
          // Dia vazio não vira cabeçalho órfão — exceto hoje, que a pessoa
          // procura na tela para se situar.
          // Fim de semana escondido NUNCA esconde dia com serviço: card
          // invisível é pior que coluna vazia — se marcaram sábado, ele aparece.
          .filter((d) => {
            const dow = d.data.getDay();
            const fds = dow === 0 || dow === 6;
            if (fds && !fimDeSemana && d.itens.length === 0) return false;
            return d.itens.length > 0 || d.chave === hojeIso;
          })
          .map((d) => (
            <div key={d.chave} id={`dia-${d.chave}`} className="mb-3">
              <div
                /* mb-3 (16px), e nao mb-2: medido no DOM havia 16px acima da
                   faixa do dia (a margem do bloco anterior) contra 8px abaixo
                   dela, e o numero ficava colado no primeiro card. Igualado
                   pela distancia maior, a pedido dele. */
                className="d-flex align-items-center gap-2 mb-3"
                role="button"
                onClick={() => alternarDia(d.chave)}
                title={recolhido(d.chave) ? 'Mostrar o dia' : 'Recolher o dia'}
              >
                <span
                  className={`dia-numero ${
                    d.chave === hojeIso
                      ? 'bg-theme-1 text-white'
                      : 'bg-theme-1-subtle text-theme-1'
                  }`}
                >
                  {d.data.toLocaleDateString('pt-BR', { day: '2-digit' })}
                </span>
                <span className="fw-medium text-capitalize">
                  {d.data.toLocaleDateString('pt-BR', { weekday: 'long' })}
                </span>
                {d.chave === hojeIso && (
                  <span className="badge text-bg-light border">hoje</span>
                )}
                {/* A viagem é do DIA, não do card: saber que a terça inteira é
                    uma ida a Trancoso muda como ele se organiza. */}
                {(() => {
                  const viagemId = d.itens.find((v) => v.viagem_id)?.viagem_id;
                  const nome = nomeViagem(viagemId);
                  return nome ? (
                    <span
                      className="badge rounded-pill bg-theme-1 d-inline-flex align-items-center gap-1"
                      /* Nome curto no chip, roteiro inteiro no toque longo /
                         mouse — o card do celular não comporta o itinerário. */
                      title={nomeViagemCompleto(viagemId)}
                    >
                      <i className="bi bi-signpost-2"></i>
                      {nome}
                    </span>
                  ) : null;
                })()}
                <span className="text-secondary small ms-auto">
                  {d.itens.length === 0
                    ? 'sem serviço'
                    : d.itens.length === 1
                      ? '1 serviço'
                      : `${d.itens.length} serviços`}
                </span>
                {d.itens.length > 0 && (
                  <i
                    className={`bi ${
                      recolhido(d.chave) ? 'bi-chevron-down' : 'bi-chevron-up'
                    } text-secondary`}
                  ></i>
                )}
              </div>

              {!recolhido(d.chave) &&
                d.itens.map((v) => (
                <div
                  key={v.id}
                  className={`card mb-2 card-barra card-barra-${corDo(v)}`}
                  role="button"
                  onClick={() => setDetalhe(v)}
                >
                  <div className="card-body py-3">
                    <div className="d-flex align-items-start gap-2">
                      <div className="flex-grow-1 lh-sm">
                        {/* Hora primeiro quando existe: é o que muda a ordem do
                            dia dele. Sem hora é o normal na operação. */}
                        <div className="d-flex align-items-center gap-2 mb-1">
                          {/* `v.hora` direto: `data` é só 'YYYY-MM-DD', e montar
                              um Date com ela dava meia-noite UTC — 21:00 em
                              Brasília, igual em todos os cards. */}
                          {v.hora && <span className="fw-semibold">{v.hora}</span>}
                          {v.os_codigo ? (
                            <span className="small text-secondary">
                              {v.os_codigo}
                              {v.os_tipo && ABREV_OS[v.os_tipo] && (
                                <span
                                  className="fw-semibold ms-1"
                                  style={{ color: `var(--bs-${corDo(v)})` }}
                                >
                                  | {ABREV_OS[v.os_tipo]}
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="badge text-bg-light border">
                              {ROTULO_TIPO[v.tipo] ?? v.tipo}
                            </span>
                          )}
                        </div>
                        <div className="fw-medium">
                          {v.cliente_nome ?? v.titulo ?? 'Serviço'}
                        </div>
                        {v.endereco_resumo && (
                          <div className="text-secondary small mt-1">
                            <i className="bi bi-geo-alt me-1"></i>
                            {v.endereco_resumo}
                          </div>
                        )}
                        {/* A pauta é o "o que fazer nesta ida" — é a razão de
                            ele abrir a tela. */}
                        {v.pauta && (
                          <div className="small mt-2">
                            <i className="bi bi-card-text me-1 text-secondary"></i>
                            {v.pauta}
                          </div>
                        )}
                      </div>
                      {v.endereco_resumo && (
                        // Abre o mapa no app nativo do celular. É a única ação
                        // desta tela: ele não edita nada aqui.
                        <a
                          className="btn btn-sm btn-outline-theme flex-shrink-0"
                          onClick={(e) => e.stopPropagation()}
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            v.endereco_resumo,
                          )}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Abrir no mapa"
                        >
                          <i className="bi bi-map"></i>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                ))}
            </div>
          ))
      )}

      {/* DETALHE — só leitura. O instalador não edita nada; se algo estiver
          errado, quem corrige é quem marcou.

          `fullscreen="sm-down"`: no celular o modal ocupa a tela inteira e vira
          uma página na prática, sem duplicar tela. No computador segue modal. */}
      <Modal
        show={detalhe !== null}
        onHide={() => setDetalhe(null)}
        centered
        fullscreen="sm-down"
      >
        {detalhe && (
          <>
            <Modal.Header closeButton>
              <div>
                <div className="small text-secondary">
                  {detalhe.os_codigo ?? ROTULO_TIPO[detalhe.tipo] ?? detalhe.tipo}
                  {detalhe.os_tipo && ABREV_OS[detalhe.os_tipo] && (
                    <span
                      className="fw-semibold ms-1"
                      style={{ color: `var(--bs-${corDo(detalhe)})` }}
                    >
                      | {ABREV_OS[detalhe.os_tipo]}
                    </span>
                  )}
                </div>
                <Modal.Title as="h6" className="mb-0">
                  {detalhe.cliente_nome ?? detalhe.titulo ?? 'Serviço'}
                </Modal.Title>
              </div>
            </Modal.Header>
            <Modal.Body>
              <div className="d-flex align-items-center gap-2 mb-3">
                <i className="bi bi-calendar-event text-secondary"></i>
                <span>
                  {new Date(`${detalhe.data}T12:00:00`).toLocaleDateString('pt-BR', {
                    weekday: 'long',
                    day: '2-digit',
                    month: 'long',
                  })}
                </span>
                {detalhe.hora && (
                  <span className="badge bg-theme-1-subtle text-theme-1">
                    {detalhe.hora}
                  </span>
                )}
              </div>

              {detalhe.endereco_resumo && (
                <div className="d-flex align-items-start gap-2 mb-3">
                  <i className="bi bi-geo-alt text-secondary mt-1"></i>
                  <div className="flex-grow-1">{detalhe.endereco_resumo}</div>
                </div>
              )}

              {nomeViagem(detalhe.viagem_id) && (
                <div className="d-flex align-items-center gap-2 mb-3">
                  <i className="bi bi-signpost-2 text-secondary"></i>
                  <span>
                    {/* No detalhe há largura: aqui vale o roteiro por extenso,
                        não o apelido — é onde ele confere para onde vai. */}
                    Faz parte da viagem{' '}
                    <strong>
                      {nomeViagemCompleto(detalhe.viagem_id) ??
                        nomeViagem(detalhe.viagem_id)}
                    </strong>
                  </span>
                </div>
              )}

              {/* A pauta é a razão de ele abrir o card: o que fazer nesta ida. */}
              <div className="card adminuiux-card">
                <div className="card-body py-3">
                  <div className="small text-secondary mb-1">
                    <i className="bi bi-card-text me-1"></i>O que fazer
                  </div>
                  {detalhe.pauta ? (
                    <div>{detalhe.pauta}</div>
                  ) : (
                    <div className="text-secondary fst-italic">
                      Nada anotado para esta ida.
                    </div>
                  )}
                </div>
              </div>
            </Modal.Body>
            <Modal.Footer>
              {detalhe.endereco_resumo && (
                <a
                  className="btn btn-theme"
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    detalhe.endereco_resumo,
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="bi bi-map me-1"></i>Abrir no mapa
                </a>
              )}
              <button
                type="button"
                className="btn btn-link"
                onClick={() => setDetalhe(null)}
              >
                Fechar
              </button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </CustomMolduraPagina>
  );
}
